<template>
  <section>
    <!-- 选择品牌弹窗 -->
    <div class="car-popup" v-if="showBrandPopup">
      <div class="car-popup-mask" @click="close"></div>
      <div class="car-popup-content">
        <div class="car-popup-header">
          <span class="close-btn" @click="close">×</span>
          <span class="car-popup-title">选择品牌</span>
        </div>
        <div class="car-popup-body">
          <div class="brand-list">
            <div
              v-for="letter in letterList"
              :key="letter"
              :ref="`letter-${letter}`"
            >
              <div v-if="groupedBrandList[letter].length" class="letter-group">
                <div class="letter-title">{{ letter }}</div>
                <div
                  class="brand-item"
                  v-for="brand in groupedBrandList[letter]"
                  :key="brand.id"
                  @click="selectBrand(brand)"
                >
                  <img
                    v-if="brand.logo"
                    :src="brand.logo"
                    class="brand-logo"
                    alt=""
                    loading="lazy"
                  />
                  <span class="brand-name">{{ brand.name }}</span>
                </div>
              </div>
            </div>
          </div>
          <div class="letter-index">
            <div
              v-for="letter in letterList"
              :key="letter"
              @click="scrollToLetter(letter)"
              :class="{ active: selectedLetter === letter }"
            >
              {{ letter }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 选择车系弹窗 -->
    <div class="car-popup" v-if="showSeriesPopup">
      <div class="car-popup-mask" @click="closeSeriesPopup"></div>
      <div class="car-popup-content">
        <div class="car-popup-header">
          <span class="close-btn" @click="closeSeriesPopup">×</span>
          <span class="car-popup-title">选择车系</span>
        </div>
        <div class="car-popup-body">
          <div class="series-brand-info" v-if="selectedBrand">
            <img
              v-if="selectedBrand.logo"
              :src="selectedBrand.logo"
              class="series-brand-logo"
              alt=""
            />
            <span>{{ selectedBrand.name }}</span>
          </div>
          <div class="series-list">
            <div
              class="series-item"
              v-for="series in seriesList"
              :key="series.id"
              @click="selectSeries(series)"
            >
              <img
                v-if="series.img"
                :src="series.img"
                class="series-img"
                alt=""
                loading="lazy"
              />
              <span class="series-name">{{ series.name }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 选择车型弹窗 -->
    <div class="car-popup" v-if="showModelPopup">
      <div class="car-popup-mask" @click="closeModelPopup"></div>
      <div class="car-popup-content">
        <div class="car-popup-header">
          <span class="close-btn" @click="closeModelPopup">×</span>
          <span class="car-popup-title">选择车型</span>
        </div>
        <div class="car-popup-body">
          <!-- 年份标签栏 -->
          <div class="model-year-tabs">
            <div
              class="year-tab"
              v-for="year in yearList"
              :key="year"
              :class="{ active: selectedYear === year }"
              @click="scrollToYear(year)"
            >
              {{ year }}款
            </div>
          </div>
          <!-- 车型列表 -->
          <div class="model-list">
            <div
              v-for="year in yearList"
              :key="year"
              class="model-year-group"
              :ref="`year-${year}`"
            >
              <div class="model-year-title">{{ year }}款</div>
              <div
                class="model-item"
                v-for="model in groupedModelList[year]"
                :key="model.id"
                @click="selectModel(model)"
              >
                <span class="model-name">{{ model.name }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { Toast } from "vant";
import { getCarSeriesInfoReq } from "../api/swap.js";

export default {
  name: "CarSelector",
  props: {
    platformParams: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      showBrandPopup: false,
      showSeriesPopup: false,
      showModelPopup: false,
      brandList: [],
      seriesList: [],
      modelList: [],
      selectedBrand: null,
      selectedSeries: null,
      selectedYear: "",
      selectedLetter: "",
      letterList: [
        "A",
        "B",
        "C",
        "D",
        "E",
        "F",
        "G",
        "H",
        "I",
        "J",
        "K",
        "L",
        "M",
        "N",
        "O",
        "P",
        "Q",
        "R",
        "S",
        "T",
        "U",
        "V",
        "W",
        "X",
        "Y",
        "Z",
      ],
    };
  },
  computed: {
    groupedBrandList() {
      const groups = {};
      this.letterList.forEach((letter) => {
        groups[letter] = [];
      });
      this.brandList.forEach((brand) => {
        const letter = brand.firstLetter
          ? brand.firstLetter.toUpperCase()
          : "#";
        if (groups[letter]) {
          groups[letter].push(brand);
        }
      });
      return groups;
    },
    // 年份列表（去重并排序）
    yearList() {
      const years = [
        ...new Set(this.modelList.map((item) => item.year)),
      ].filter((year) => year);
      return years.sort((a, b) => b - a);
    },
    // 按年份分组的车型列表
    groupedModelList() {
      const groups = {};
      this.yearList.forEach((year) => {
        groups[year] = [];
      });
      this.modelList.forEach((model) => {
        if (model.year && groups[model.year]) {
          groups[model.year].push(model);
        }
      });
      return groups;
    },
  },
  methods: {
    async open() {
      if (this.brandList.length === 0) {
        await this.getBrandList();
      }
      this.showBrandPopup = true;
    },
    async getBrandList() {
      try {
        const { data } = await getCarSeriesInfoReq({
          type: 2,
          PlatformParam: this.platformParams,
        });
        console.log(data);
        const list = data && data.data ? data.data : data;
        this.brandList = Array.isArray(list)
          ? list.map((item) => ({
              id: item.brandId,
              name: item.brandName,
              logo: item.logo,
              firstLetter: item.initial,
            }))
          : [];
      } catch (error) {
        Toast("获取品牌列表失败");
      }
    },
    async getSeriesList(brandId) {
      try {
        const { data } = await getCarSeriesInfoReq({
          type: 3,
          MediaId: brandId,
          PlatformParam: this.platformParams,
        });
        const rawList = data && data.data ? data.data : data;
        const list = Array.isArray(rawList) ? rawList : [];
        this.seriesList = list.map((item) => ({
          id: item.seriesId || item.id,
          name: item.seriesName || item.name,
          img: item.noBackLogo || item.logo || item.pngImageUrl,
        }));
      } catch (error) {
        Toast("获取车系列表失败");
      }
    },
    async getModelList(seriesId) {
      try {
        const { data } = await getCarSeriesInfoReq({
          type: 4,
          MediaId: seriesId,
          PlatformParam: this.platformParams,
        });
        const rawList = data && data.data ? data.data : data;
        const list = Array.isArray(rawList) ? rawList : [];
        this.modelList = list.map((item) => ({
          id: item.specId || item.modelId || item.id,
          name: item.specName || item.modelName || item.name,
          year: item.year || item.yearName,
          logo: item.logo,
        }));
      } catch (error) {
        Toast("获取车型列表失败");
      }
    },
    close() {
      this.showBrandPopup = false;
      this.showSeriesPopup = false;
      this.showModelPopup = false;
      this.selectedBrand = null;
      this.selectedSeries = null;
      this.$emit("cancel");
    },
    selectBrand(brand) {
      this.selectedBrand = brand;
      this.showSeriesPopup = true;
      this.getSeriesList(brand.id);
    },
    closeSeriesPopup() {
      this.showSeriesPopup = false;
      this.selectedSeries = null;
    },
    selectSeries(series) {
      this.selectedSeries = series;
      this.showModelPopup = true;
      this.getModelList(series.id);
    },
    closeModelPopup() {
      this.showModelPopup = false;
    },
    selectModel(model) {
      this.$emit("confirm", {
        brandId: this.selectedBrand.id,
        brandName: this.selectedBrand.name,
        seriesId: this.selectedSeries.id,
        seriesName: this.selectedSeries.name,
        modelId: model.id,
        modelName: model.name,
        logo: this.selectedSeries.img,
      });
      this.close();
    },
    scrollToLetter(letter) {
      this.selectedLetter = letter;
      const el = this.$refs[`letter-${letter}`];
      if (el && el[0]) {
        el[0].scrollIntoView({ behavior: "smooth" });
      }
    },
    scrollToYear(year) {
      this.selectedYear = year;
      const el = this.$refs[`year-${year}`];
      if (el && el[0]) {
        el[0].scrollIntoView({ behavior: "smooth" });
      }
    },
  },
};
</script>

<style scoped>
/* 选择品牌车系车型弹窗 */
.car-popup {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 200;
}

.car-popup-mask {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
}

.car-popup-content {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 80%;
  background: #fff;
  border-radius: 0.5rem 0.5rem 0 0;
  display: flex;
  flex-direction: column;
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}

.car-popup-header {
  position: relative;
  height: 3rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
}

.car-popup-header .close-btn {
  position: absolute;
  left: 0.75rem;
  font-size: 1.5rem;
  color: #999;
  cursor: pointer;
  line-height: 1;
}

.car-popup-title {
  font-size: 1rem;
  font-weight: 600;
  color: #111e36;
}

.car-popup-body {
  flex: 1;
  overflow-y: auto;
  position: relative;
}

/* 品牌列表 */
.brand-list {
  padding: 0 0.75rem 0.75rem 0.75rem;
}

.letter-title {
  font-size: 1.2rem;
  color: #828ca0;
  padding: 0.5rem 0;
  background: #fff;
  position: sticky;
  top: 0;
  box-sizing: border-box;
}

.brand-item {
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  padding: 1rem 0;
  border-bottom: 1px solid #f5f5f5;
}

.brand-logo {
  width: 2.75rem;
  height: 2.75rem;
  object-fit: contain;
  margin-right: 0.75rem;
  flex-shrink: 0;
}

.brand-name {
  font-size: 1rem;
  color: #111e36;
}

.letter-index {
  position: fixed;
  right: 0.25rem;
  top: 60%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 10;
}

.letter-index div {
  width: 1.5rem;
  height: 1.5rem;
  line-height: 1.5rem;
  text-align: center;
  font-size: 0.85rem;
  color: #1989fa;
  border-radius: 50%;
}

.letter-index div.active {
  background: #1989fa;
  color: #fff;
}

/* 车系列表 */
.series-brand-info {
  display: flex;
  align-items: center;
  padding: 0.75rem;
  font-size: 1rem;
  font-weight: 600;
  color: #111e36;
  border-bottom: 1px solid #f0f0f0;
}

.series-brand-logo {
  width: 2rem;
  height: 2rem;
  object-fit: contain;
  margin-right: 0.75rem;
}

.series-list {
  padding: 0 0.75rem 0.75rem 0.75rem;
}

.series-item {
  font-size: 1rem;
  display: flex;
  align-items: center;
  padding: 1rem 0;
  border-bottom: 1px solid #f5f5f5;
}

.series-img {
  width: 4rem;
  height: 3.5rem;
  object-fit: contain;
  margin-right: 0.75rem;
  flex-shrink: 0;
}

.series-name {
  font-size: 1rem;
  color: #111e36;
}

/* 车型年份标签栏 */
.model-year-tabs {
  display: flex;
  overflow-x: auto;
  padding: 0.75rem;
  background: #fff;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
  -webkit-overflow-scrolling: touch;
}

.model-year-tabs::-webkit-scrollbar {
  display: none;
}

.year-tab {
  padding: 0.5rem 1rem;
  margin-right: 0.75rem;
  background: #f5f5f5;
  border-radius: 1.5rem;
  font-size: 1rem;
  color: #111e36;
  white-space: nowrap;
  flex-shrink: 0;
}

.year-tab.active {
  background: #1989fa;
  color: #fff;
}

/* 车型列表 */
.model-list {
  padding: 0 0.75rem 0.75rem 0.75rem;
}

.model-year-group {
  margin-bottom: 0.5rem;
}

.model-year-title {
  font-size: 1rem;
  color: #828ca0;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 0.5rem;
}

.model-item {
  padding: 1rem 0;
  border-bottom: 1px solid #f5f5f5;
}

.model-name {
  font-size: 1.1rem;
  color: #111e36;
}
</style>



// WEBPACK FOOTER //
// src/components/car-selector.vue